import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

/**
 * API URL detection: use relative '/api' in production (same domain),
 * use REACT_APP_BACKEND_URL only in development/preview.
 * This prevents cross-origin issues on mobile browsers (Safari/WebKit).
 */
function getApiUrl() {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  // In production: always use relative path (same domain)
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    // If we're NOT on localhost and NOT on the preview domain, use relative path
    if (host !== 'localhost' && !host.includes('preview.emergentagent.com')) {
      return '/api';
    }
  }
  return envUrl ? `${envUrl}/api` : '/api';
}

const API = getApiUrl();

const AuthContext = createContext(null);

/**
 * Auth strategy:
 * - Primary: httpOnly cookies (set by backend on login/register/refresh)
 * - Fallback: sessionStorage + localStorage (for preview envs where cross-site cookies are blocked)
 * The backend sets httpOnly cookies AND returns tokens in the JSON body.
 * The frontend stores the fallback tokens only for environments where cookies don't work.
 */
const tokenStore = {
  get: (key) => sessionStorage.getItem(key) || localStorage.getItem(key),
  set: (key, value) => {
    sessionStorage.setItem(key, value);
    localStorage.setItem(key, value);
  },
  remove: (key) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  }
};

const apiClient = axios.create({
  baseURL: API,
  withCredentials: true
});

// Attach fallback token only if cookies aren't working
apiClient.interceptors.request.use((config) => {
  // Always send the fallback Bearer header — the backend checks cookies first,
  // then falls back to the Authorization header. This ensures both paths work.
  const token = tokenStore.get('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !error.config._retry) {
      error.config._retry = true;
      try {
        // Try cookie-based refresh first (httpOnly cookie sent automatically)
        const refreshResponse = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        if (refreshResponse.status === 200) {
          // If refresh returns a new access_token in body, store it as fallback
          if (refreshResponse.data?.access_token) {
            tokenStore.set('access_token', refreshResponse.data.access_token);
          }
          return apiClient(error.config);
        }
      } catch {
        tokenStore.remove('access_token');
        tokenStore.remove('refresh_token');
      }
    }
    // Retry transient failures (network error / 5xx / timeout) on GET requests
    const cfg = error.config || {};
    const isGet = (cfg.method || 'get').toLowerCase() === 'get';
    const isTransient = !error.response || error.response.status >= 500;
    cfg._retryCount = cfg._retryCount || 0;
    if (isGet && isTransient && cfg._retryCount < 2) {
      cfg._retryCount += 1;
      await new Promise((r) => setTimeout(r, 500 * cfg._retryCount));
      return apiClient(cfg);
    }
    return Promise.reject(error);
  }
);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Try cookie-based auth first (httpOnly cookie sent via withCredentials)
    // Then fallback to stored token
    const hasToken = tokenStore.get('access_token');
    try {
      const { data } = await apiClient.get('/auth/me');
      setUser(data);
    } catch {
      // If no cookie AND no token, user is not authenticated
      if (hasToken) {
        tokenStore.remove('access_token');
        tokenStore.remove('refresh_token');
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (name, password) => {
    tokenStore.remove('access_token');
    tokenStore.remove('refresh_token');
    // withCredentials: true ensures the httpOnly cookie is set by the browser
    const { data } = await axios.post(`${API}/auth/login`, { name, password }, { withCredentials: true });
    // Store tokens as fallback for environments where cookies are blocked
    if (data.access_token) tokenStore.set('access_token', data.access_token);
    if (data.refresh_token) tokenStore.set('refresh_token', data.refresh_token);
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (name, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, password }, { withCredentials: true });
    if (data.access_token) tokenStore.set('access_token', data.access_token);
    if (data.refresh_token) tokenStore.set('refresh_token', data.refresh_token);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await apiClient.post('/auth/logout'); } catch (err) { console.error('Logout API failed:', err); }
    tokenStore.remove('access_token');
    tokenStore.remove('refresh_token');
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, login, register, logout }), [user, loading, login, register, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}

export { AuthProvider, useAuth, apiClient };
